/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  get, omit, map, isEmpty, set
} from 'lodash';
import Form from './Form';
import {
  isEmail,
  isPostalCode,
  isPhoneNumber,
  formatPostalCode,
} from '../lib/utils';
import PhoneNumberInput from './PhoneNumberInput';
import { preferredContactMethods, canadaProvinces } from '../lib/consts';
import useDialog from '../hooks/useDialog';
import { title as termsTitle, content as termsContent } from './ServiceTerms';
import { title as refundPolicyTitle, content as refundPolicyContent } from './RefundPolicy';

const Base = styled.div`
  max-width: 30rem;
  margin-bottom: 1.5rem;
  form {
    max-width: 30rem;

    .field-radio-group {
      .radio-inline {
        margin-right: 1em;
        input {
          margin-right: 0.3em;
        }
      }
    }
    .buttons {
      text-align: right;
    }
    label {
      display: flex;
      input {
        margin-top: 0.35rem;
      }
      span a {
        cursor: pointer;
      }
      .field-description {
        text-align: justify;
      }
    }
  }
  .modal-body {
    text-align: justify;
  }
`;

function getFormSchema({
  registerFormData, group,
}) {
  const checkDuplicates = get(group, 'config.checkDuplicates', false);
  const showDuplicatesInput = checkDuplicates || get(group, 'config.uniqueCodeDescription') != null;
  const preferredContactMethod = get(registerFormData, 'preferredContactMethod');
  return {
    type: 'object',
    required: [
      'firstName',
      'lastName',
      'postalCode',
      'address',
      'preferredContactMethod',
      'city',
      'state',
      ...preferredContactMethod === preferredContactMethods.email.value ? ['email', 'emailConfirmation'] : [],
      ...checkDuplicates ? ['grpUniqueCode'] : [],
    ],
    properties: {
      firstName: {
        type: 'string',
        title: 'First Name',
      },
      lastName: {
        type: 'string',
        title: 'Last Name',
      },
      ...(showDuplicatesInput ? {
        grpUniqueCode: {
          type: 'string',
          title: 'Unique Code',
        },
      } : {}),
      address: {
        type: 'string',
        title: 'Address',
      },
      city: {
        type: 'string',
        title: 'City',
      },
      state: {
        type: 'string',
        title: 'Province',
        enum: map(canadaProvinces, 'abbr'),
        enumNames: map(canadaProvinces, 'name'),
      },
      postalCode: {
        type: 'string',
        title: 'Postal Code',
      },
      email: {
        type: 'string',
        title: 'E-mail',
      },
      emailConfirmation: {
        type: 'string',
        title: 'Confirm E-mail',
      },
      phone: {
        type: 'string',
        title: 'Mobile Phone',
        description: 'Please provide your mobile phone number if you wish to receive updates by text message.',
      },
      homePhone: {
        type: 'string',
        title: 'Home Phone',
        description: 'If you do not have an email address or mobile phone, please provide a home telephone number for us to contact you. ',
      },
      preferredContactMethod: {
        type: 'string',
        title: 'Preferred Contact Method',
        enum: map(preferredContactMethods, 'value'),
        enumNames: map(preferredContactMethods, 'label'),
      },
      agreed: {
        type: 'boolean',
      },
    },
  };
}

function getFormUiSchema({
  group,
  registerFormData,
  showTermDialog,
  showRefundPolicyDialog,
}) {
  // const preferredContactMethod = get(registerFormData, 'preferredContactMethod');
  return {
    preferredContactMethod: {
      'ui:widget': 'radio',
      'ui:options': {
        inline: true,
      },
    },
    phone: {
      'ui:widget': PhoneNumberInput,
    },
    grpUniqueCode: {
      'ui:description': get(group, 'config.uniqueCodeDescription'),
    },
    agreed: {
      'ui:description': (
        <span>
          I agree to receive membership renewals, updates,
          and information about new services or savings offered to Genrus United members.
          Upon joining Genrus United I agree with the&nbsp;
          <a
            role="button"
            className="text-primary"
            onClick={(evt) => { evt.preventDefault(); evt.stopPropagation(); showTermDialog(); }}
          >
            terms & conditions
          </a>
          <span>&nbsp;and&nbsp;</span>
          <a
            role="button"
            className="text-primary"
            onClick={(evt) => {
              evt.preventDefault();
              evt.stopPropagation();
              showRefundPolicyDialog();
            }}
          >
            refund policy
          </a>
          <span>.&nbsp;</span>
          I can opt out from these emails at any time.
        </span>
      ),
    },
    // ...preferredContactMethod === preferredContactMethods.none.value ? {
    //   email: {
    //     'ui:readonly': true,
    //   },
    //   emailConfirmation: {
    //     'ui:readonly': true,
    //   },
    //   phone: {
    //     'ui:readonly': true,
    //   },
    // } : {
    //   },
  };
}

function validate(formData, errors) {
  if (formData.preferredContactMethod === preferredContactMethods.phone.value) {
    if (isEmpty(formData.phone) && isEmpty(formData.homePhone)) {
      errors.phone.addError('Please input your mobile phone number or home phone number.');
    }
  }
  if (formData.preferredContactMethod === preferredContactMethods.email.value) {
    if (isEmpty(formData.email)) {
      errors.email.addError('Please input your e-mail address.');
    }
    if (formData.email !== formData.emailConfirmation) {
      errors.emailConfirmation.addError('E-mails don\'t match.');
    }
  }
  if (!isEmpty(formData.phone) && !isPhoneNumber(formData.phone)) {
    errors.phone.addError('Please use a valid phone number.');
  }
  if (!isEmpty(formData.homePhone) && !isPhoneNumber(formData.homePhone)) {
    errors.homePhone.addError('Please use a valid phone number.');
  }
  if (!isEmpty(formData.email) && !isEmail(formData.email)) {
    errors.email.addError('Please use a valid E-mail address');
  }
  if (isPostalCode(formData.postalCode) !== true) {
    errors.postalCode.addError('Please use a valid postal code');
  }
  return errors;
}

export default function RegisterForm({
  links, onSubmit, group, registerFormData,
  setRegisterFormData, resetRegisterFormData,
  onReview, reviewMode, onBack,
}) {
  const preferredContactMethod = get(registerFormData, 'preferredContactMethod');
  const nonePreferred = 
    preferredContactMethod === preferredContactMethods.phone.value && isEmpty(get(registerFormData, 'phone'));
  const agreed = get(registerFormData, 'agreed');
  const { dialog: termDialog, showDialog: showTermDialog } = useDialog({
    type: 'alert', title: termsTitle, message: termsContent, width: 600,
  });
  const { dialog: refundPolicyDialog, showDialog: showRefundPolicyDialog } = useDialog({ type: 'alert', title: refundPolicyTitle, message: refundPolicyContent });
  const formSchema = getFormSchema({
    registerFormData, group,
  });
  const formUiSchema = getFormUiSchema({
    group, registerFormData, showTermDialog, showRefundPolicyDialog,
  });

  return (
    <Base>
      <Form
        disabled={reviewMode}
        showErrorList={false}
        data={registerFormData}
        schema={formSchema}
        uiSchema={formUiSchema}
        validate={validate}
        onChange={({ formData: { postalCode, ...data } }) => {
          setRegisterFormData({
            ...data,
            postalCode: formatPostalCode(postalCode),
          });
        }}
        onSubmit={() => {
          if (reviewMode) {
            onSubmit(set(omit(registerFormData, [
              'emailConfirmation',
              'agreed',
              'province'
            ]), 'preferredContactMethod', nonePreferred ? 'NONE' : preferredContactMethod));
          } else {
            onReview();
          }
        }}
      >
        {links}
        {
          !reviewMode && ([
            <button
              type="button"
              key="clear"
              className="btn btn-light"
              onClick={resetRegisterFormData}
            >
              Clear
            </button>,
            <button
              type="submit"
              key="continue"
              className="btn btn-primary"
              hidden={reviewMode}
              disabled={!agreed}
            >
              Continue
              &nbsp;
              <i className="fa fa-chevron-right" />
            </button>,
          ])
        }
        {
          reviewMode && ([
            <button
              key="back"
              type="button"
              className="btn btn-light"
              onClick={onBack}
            >
              Back
            </button>,
            <button key="submit" type="submit" className="btn btn-primary">
              Confirm &amp; Finish
              &nbsp;
              <i className="fa fa-check" />
            </button>,
          ])
        }
      </Form>
      {termDialog}
      {refundPolicyDialog}
    </Base>
  );
}

RegisterForm.propTypes = {
  links: PropTypes.node,
  onSubmit: PropTypes.func,
  group: PropTypes.shape({}),
  tokenData: PropTypes.shape({}),
  policies: PropTypes.shape({
    totalCount: PropTypes.number,
    nodes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  registerFormData: PropTypes.shape({}),
  setRegisterFormData: PropTypes.func,
  resetRegisterFormData: PropTypes.func,
  reviewMode: PropTypes.bool,
  onReview: PropTypes.func,
  onBack: PropTypes.func.isRequired,
};

RegisterForm.defaultProps = {
  links: undefined,
  onSubmit: () => null,
  registerFormData: {},
  setRegisterFormData: () => null,
  resetRegisterFormData: () => null,
  group: {},
  tokenData: {},
  policies: {
    totalCount: 0,
    nodes: [],
  },
  reviewMode: false,
  onReview: () => null,
};
